import React, { useState, useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";

const ResetPassword = ({ boxedLogo, boxedLogoLight, isLightTheme }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = searchParams.get("token");
  const accountBasePath = location.pathname.startsWith("/publishers")
    ? "/publishers"
    : "/advertisers";

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      setValidatingToken(false);
      return;
    }

    // Validate token
    const validateToken = async () => {
      try {
        const response = await fetch(`/password_resets/${token}`);
        const data = await response.json();

        if (data.valid) {
          setTokenValid(true);
        } else {
          setError(data.error || "Invalid or expired reset link.");
        }
      } catch (err) {
        setError("Failed to validate reset link. Please try again.");
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!password || !passwordConfirmation) {
      setError("Please enter and confirm your new password.");
      setLoading(false);
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/password_resets/${token}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password,
          password_confirmation: passwordConfirmation,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password reset successfully! Please sign in with your new password.");
        navigate(`${accountBasePath}/sign-in`);
      } else {
        setError(data.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="sign-up-wrapper rainbow-section-gap">
        <div className="sign-up-box bg-flashlight">
          <div className="signup-box-top top-flashlight light-xl">
            <img
              className={`boxed-logo ${
                isLightTheme ? "boxed-logo-dark" : "boxed-logo-light"
              }`}
              src={isLightTheme ? boxedLogo : boxedLogoLight}
              width={180}
              alt="reset-password logo"
            />
          </div>
          <div className="separator-animated animated-true"></div>
          <div className="signup-box-bottom">
            <div className="signup-box-content">
              <h4 className="title">Validating Reset Link...</h4>
              <p>Please wait while we validate your password reset link.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="sign-up-wrapper rainbow-section-gap">
        <div className="sign-up-box bg-flashlight">
          <div className="signup-box-top top-flashlight light-xl">
            <img
              className={`boxed-logo ${
                isLightTheme ? "boxed-logo-dark" : "boxed-logo-light"
              }`}
              src={isLightTheme ? boxedLogo : boxedLogoLight}
              width={180}
              alt="reset-password logo"
            />
          </div>
          <div className="separator-animated animated-true"></div>
          <div className="signup-box-bottom">
            <div className="signup-box-content">
              <h4 className="title">Invalid Reset Link</h4>
              <div className="alert alert-danger">{error}</div>
              <div className="signup-box-footer">
                <div className="bottom-text">
                  <Link
                    className="btn-read-more ps-2"
                    to={`${accountBasePath}/forgot-password`}
                  >
                    <span>Request New Reset Link</span>
                  </Link>
                  <span> or </span>
                  <Link
                    className="btn-read-more ps-2"
                    to={`${accountBasePath}/sign-in`}
                  >
                    <span>Back to Sign In</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-up-wrapper rainbow-section-gap">
      <div className="sign-up-box bg-flashlight">
        <div className="signup-box-top top-flashlight light-xl">
          <img
            className={`boxed-logo ${
              isLightTheme ? "boxed-logo-dark" : "boxed-logo-light"
            }`}
            src={isLightTheme ? boxedLogo : boxedLogoLight}
            width={180}
            alt="reset-password logo"
          />
        </div>
        <div className="separator-animated animated-true"></div>
        <div className="signup-box-bottom">
          <div className="signup-box-content">
            <h4 className="title">Reset Your Password</h4>
            <form autoComplete="off" onSubmit={handleSubmit}>
              <div className="input-section password-section">
                <div className="icon">
                  <i className="feather-lock"></i>
                </div>
                <input
                  type="password"
                  placeholder="New Password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="input-section password-section">
                <div className="icon">
                  <i className="feather-lock"></i>
                </div>
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  disabled={loading}
                />
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <button type="submit" className="btn-default" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>
          <div className="signup-box-footer">
            <div className="bottom-text">
              <Link className="btn-read-more ps-2" to={`${accountBasePath}/sign-in`}>
                <span>Back to Sign In</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
